import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Check, Trash2, RefreshCw, AlertTriangle, ArrowRight, Link, Clock, MapPin, 
  Package, AlertCircle, Calendar, FileText, Image as ImageIcon, PenTool, ExternalLink, ShieldCheck, Eye 
} from 'lucide-react';
import { formatLocalTimeHHMM, localTimeToISO } from '../utils/dateUtils';

const resolveAssetUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('blob:') || url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return url.startsWith('/') ? url : `/${url}`;
};

export default function StopCardModal({
  stop,
  isOpen,
  onClose,
  onSaveStop,
  onDeleteStop,
  jobs = [],
  clients = [],
  rounds = [],
  stops = [],
  unassignedStops = [],
  stopDependencies = [],
  onReloadData
}) {
  if (!isOpen || !stop) return null;

  const saveTimeoutRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Attachments (Signatures, POD photos, documents) and Return Tickets
  const [attachments, setAttachments] = useState([]);
  const [returnTickets, setReturnTickets] = useState([]);
  const [loadingProofs, setLoadingProofs] = useState(false);
  const [selectedProofImage, setSelectedProofImage] = useState(null);

  const fetchProofs = async (stopId) => {
    if (!stopId) return;
    setLoadingProofs(true);
    try {
      const [attRes, ticketsRes] = await Promise.all([
        fetch(`/api/stops/${stopId}/attachments`),
        fetch(`/api/stops/${stopId}/return-tickets`)
      ]);
      if (attRes.ok) {
        const attData = await attRes.json();
        setAttachments(Array.isArray(attData) ? attData : []);
      }
      if (ticketsRes.ok) {
        const ticketsData = await ticketsRes.json();
        setReturnTickets(Array.isArray(ticketsData) ? ticketsData : []);
      }
    } catch (err) {
      console.error('Error fetching stop proofs:', err);
    } finally {
      setLoadingProofs(false);
    }
  };

  useEffect(() => {
    if (stop && stop.id) {
      fetchProofs(stop.id);
    }
  }, [stop?.id]);

  // Time extraction helper strictly adhering to Europe/Zurich timezone
  const extractTimeHHMM = (val) => formatLocalTimeHHMM(val, '');

  const getDurationMins = (arrHHMM, depHHMM) => {
    if (!arrHHMM || !depHHMM) return '';
    const [h1, m1] = arrHHMM.split(':').map(Number);
    const [h2, m2] = depHHMM.split(':').map(Number);
    if (isNaN(h1) || isNaN(m1) || isNaN(h2) || isNaN(m2)) return '';
    let diff = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (diff < 0) diff += 1440;
    return diff;
  };

  const addMinutesToTime = (timeHHMM, mins) => {
    if (!timeHHMM || isNaN(mins)) return '';
    const [h, m] = timeHHMM.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return '';
    const totalMins = (h * 60 + m + parseInt(mins)) % 1440;
    const newH = Math.floor(totalMins / 60);
    const newM = totalMins % 60;
    return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
  };

  // Initialize time windows list
  const initialTimeWindows = (() => {
    if (Array.isArray(stop.time_windows) && stop.time_windows.length > 0) {
      return stop.time_windows.map(tw => ({
        start: (tw.start || '08:00').slice(0, 5),
        end: (tw.end || '18:00').slice(0, 5)
      }));
    }
    if (stop.time_window_start) {
      return [{ start: stop.time_window_start.slice(0, 5), end: (stop.time_window_end || '18:00').slice(0, 5) }];
    }
    return [{ start: '08:00', end: '18:00' }];
  })();

  const initialActualArr = extractTimeHHMM(stop.actual_arrival);
  const initialRealDep = extractTimeHHMM(stop.real_departure);
  const initialRealDuration = getDurationMins(initialActualArr, initialRealDep);

  // Look up dependency from stopDependencies if not directly in stop object
  let initialRelPickup = stop.related_pickup_stop_id || null;
  let initialRelDelivery = stop.related_delivery_stop_id || null;
  if (!initialRelPickup && Array.isArray(stopDependencies)) {
    const depEdge = stopDependencies.find(d => d.successor_stop_id === stop.id);
    if (depEdge) initialRelPickup = depEdge.predecessor_stop_id;
  }
  if (!initialRelDelivery && Array.isArray(stopDependencies)) {
    const depEdge = stopDependencies.find(d => d.predecessor_stop_id === stop.id);
    if (depEdge) initialRelDelivery = depEdge.successor_stop_id;
  }

  const [formData, setFormData] = useState({
    id: stop.id,
    stop_number: stop.stop_number || `STOP-${stop.id}`,
    job_id: stop.job_id || '',
    client_id: stop.client_id || (clients[0]?.id || 1),
    type: stop.type || 'delivery',
    status: stop.status || 'pending',
    weight_kg: stop.weight_kg !== undefined ? stop.weight_kg : 100.0,
    volume_m3: stop.volume_m3 !== undefined ? stop.volume_m3 : 0.5,
    revenue: stop.revenue !== undefined ? stop.revenue : 0.0,
    service_time_mins: stop.service_time_mins !== undefined ? stop.service_time_mins : 10,
    sequence_order: stop.sequence_order !== undefined ? stop.sequence_order : 1,
    estimated_index: stop.estimated_index !== undefined ? stop.estimated_index : 1,
    real_index: stop.real_index !== undefined && stop.real_index !== null ? stop.real_index : '',
    estimated_arrival: extractTimeHHMM(stop.estimated_arrival) || '08:00',
    actual_arrival_time: initialActualArr,
    real_departure_time: initialRealDep,
    real_duration_mins: initialRealDuration !== '' ? initialRealDuration : '',
    delivery_date: stop.delivery_date ? String(stop.delivery_date).slice(0, 10) : new Date().toISOString().slice(0, 10),
    default_round_id: stop.default_round_id || '',
    instructions: stop.instructions || '',
    driver_notes: stop.driver_notes || '',
    payment_type: stop.payment_type || 'none',
    amount_paid: stop.amount_paid !== undefined ? stop.amount_paid : 0.0,
    time_windows: initialTimeWindows,
    related_pickup_stop_id: initialRelPickup,
    related_delivery_stop_id: initialRelDelivery,
    metadata: typeof stop.metadata === 'object' ? JSON.stringify(stop.metadata, null, 2) : (stop.metadata || '{}')
  });

  // Sync state on external stop changes
  useEffect(() => {
    if (stop && stop.id) {
      const actArr = extractTimeHHMM(stop.actual_arrival);
      const rDep = extractTimeHHMM(stop.real_departure);
      const rDur = getDurationMins(actArr, rDep);

      let rPickup = stop.related_pickup_stop_id || null;
      let rDelivery = stop.related_delivery_stop_id || null;
      if (!rPickup && Array.isArray(stopDependencies)) {
        const depEdge = stopDependencies.find(d => d.successor_stop_id === stop.id);
        if (depEdge) rPickup = depEdge.predecessor_stop_id;
      }
      if (!rDelivery && Array.isArray(stopDependencies)) {
        const depEdge = stopDependencies.find(d => d.predecessor_stop_id === stop.id);
        if (depEdge) rDelivery = depEdge.successor_stop_id;
      }

      setFormData({
        id: stop.id,
        stop_number: stop.stop_number || `STOP-${stop.id}`,
        job_id: stop.job_id || '',
        client_id: stop.client_id || (clients[0]?.id || 1),
        type: stop.type || 'delivery',
        status: stop.status || 'pending',
        weight_kg: stop.weight_kg !== undefined ? stop.weight_kg : 100.0,
        volume_m3: stop.volume_m3 !== undefined ? stop.volume_m3 : 0.5,
        revenue: stop.revenue !== undefined ? stop.revenue : 0.0,
        service_time_mins: stop.service_time_mins !== undefined ? stop.service_time_mins : 10,
        sequence_order: stop.sequence_order !== undefined ? stop.sequence_order : 1,
        estimated_index: stop.estimated_index !== undefined ? stop.estimated_index : 1,
        real_index: stop.real_index !== undefined && stop.real_index !== null ? stop.real_index : '',
        estimated_arrival: extractTimeHHMM(stop.estimated_arrival) || '08:00',
        actual_arrival_time: actArr,
        real_departure_time: rDep,
        real_duration_mins: rDur !== '' ? rDur : '',
        delivery_date: stop.delivery_date ? String(stop.delivery_date).slice(0, 10) : new Date().toISOString().slice(0, 10),
        default_round_id: stop.default_round_id || '',
        instructions: stop.instructions || '',
        driver_notes: stop.driver_notes || '',
        payment_type: stop.payment_type || 'none',
        amount_paid: stop.amount_paid !== undefined ? stop.amount_paid : 0.0,
        time_windows: initialTimeWindows,
        related_pickup_stop_id: rPickup,
        related_delivery_stop_id: rDelivery,
        metadata: typeof stop.metadata === 'object' ? JSON.stringify(stop.metadata, null, 2) : (stop.metadata || '{}')
      });
      setIsConfirmingDelete(false);
      setIsDeleting(false);
    }
  }, [stop?.id, isOpen]);

  // IN-PLACE PERSISTENCE HANDLER
  const saveInPlace = async (stateToSave) => {
    const data = stateToSave || formData;
    setSaveStatus('saving');
    try {
      const d = data.delivery_date || stop.delivery_date || new Date().toISOString().slice(0, 10);
      const actualArrIso = localTimeToISO(d, data.actual_arrival_time);
      const realDepIso = localTimeToISO(d, data.real_departure_time);

      let metaJson = {};
      try {
        metaJson = typeof data.metadata === 'string' ? JSON.parse(data.metadata) : (data.metadata || {});
      } catch (e) {
        metaJson = { raw: data.metadata };
      }

      const cleanData = {
        stop_number: data.stop_number,
        type: data.type,
        status: data.status,
        job_id: data.job_id ? parseInt(data.job_id) : null,
        client_id: data.client_id ? parseInt(data.client_id) : null,
        default_round_id: data.default_round_id ? parseInt(data.default_round_id) : null,
        delivery_date: data.delivery_date || null,
        sequence_order: data.sequence_order !== '' && data.sequence_order !== null ? parseInt(data.sequence_order) : 1,
        estimated_index: data.estimated_index !== '' && data.estimated_index !== null ? parseInt(data.estimated_index) : 1,
        real_index: data.real_index !== '' && data.real_index !== null ? parseInt(data.real_index) : null,
        weight_kg: data.weight_kg !== '' && data.weight_kg !== null ? parseFloat(data.weight_kg) : 0,
        volume_m3: data.volume_m3 !== '' && data.volume_m3 !== null ? parseFloat(data.volume_m3) : 0,
        revenue: data.revenue !== '' && data.revenue !== null ? parseFloat(data.revenue) : 0,
        service_time_mins: data.service_time_mins !== '' && data.service_time_mins !== null ? parseInt(data.service_time_mins) : 10,
        estimated_arrival: data.estimated_arrival || '08:00',
        actual_arrival: actualArrIso,
        real_departure: realDepIso,
        instructions: data.instructions,
        driver_notes: data.driver_notes,
        payment_type: data.payment_type || 'none',
        amount_paid: data.amount_paid !== '' && data.amount_paid !== null ? parseFloat(data.amount_paid) : 0,
        time_windows: data.time_windows,
        related_pickup_stop_id: data.related_pickup_stop_id ? parseInt(data.related_pickup_stop_id) : null,
        related_delivery_stop_id: data.related_delivery_stop_id ? parseInt(data.related_delivery_stop_id) : null,
        metadata: metaJson
      };

      const res = await fetch(`/api/stops/${stop.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanData)
      });

      if (res.ok) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 2500);
        if (onReloadData) onReloadData();
      } else {
        setSaveStatus('error');
      }
    } catch (err) {
      console.error('In-place save error:', err);
      setSaveStatus('error');
    }
  };

  const handleFieldChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveInPlace(updated);
    }, 500);
  };

  const handleFieldBlur = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    saveInPlace(formData);
  };

  const handleImmediateChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveInPlace(updated);
  };

  // Synchronized Arrival / Departure / Real Duration
  const handleActualArrivalChange = (val) => {
    const updated = { ...formData, actual_arrival_time: val };
    if (val && formData.real_duration_mins !== '') {
      updated.real_departure_time = addMinutesToTime(val, formData.real_duration_mins);
    } else if (val && formData.real_departure_time) {
      updated.real_duration_mins = getDurationMins(val, formData.real_departure_time);
    }
    setFormData(updated);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveInPlace(updated), 500);
  };

  const handleRealDepartureChange = (val) => {
    const updated = { ...formData, real_departure_time: val };
    if (formData.actual_arrival_time && val) {
      updated.real_duration_mins = getDurationMins(formData.actual_arrival_time, val);
    }
    setFormData(updated);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveInPlace(updated), 500);
  };

  const handleRealDurationChange = (val) => {
    const mins = val === '' ? '' : parseInt(val);
    const updated = { ...formData, real_duration_mins: mins };
    if (formData.actual_arrival_time && mins !== '') {
      updated.real_departure_time = addMinutesToTime(formData.actual_arrival_time, mins);
    }
    setFormData(updated);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveInPlace(updated), 500);
  };

  const handleEstimatedDepartureChange = (newEstDep) => {
    if (formData.estimated_arrival && newEstDep) {
      const dur = getDurationMins(formData.estimated_arrival, newEstDep);
      if (dur !== '' && !isNaN(dur) && dur >= 0) {
        const updated = { ...formData, service_time_mins: dur };
        setFormData(updated);
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => saveInPlace(updated), 500);
      }
    }
  };

  // Time Windows Management
  const handleAddTimeWindow = () => {
    const updated = {
      ...formData,
      time_windows: [...formData.time_windows, { start: '14:00', end: '18:00' }]
    };
    setFormData(updated);
    saveInPlace(updated);
  };

  const handleRemoveTimeWindow = (idx) => {
    if (formData.time_windows.length <= 1) return;
    const updated = {
      ...formData,
      time_windows: formData.time_windows.filter((_, i) => i !== idx)
    };
    setFormData(updated);
    saveInPlace(updated);
  };

  const handleTimeWindowFieldChange = (idx, field, val) => {
    const newWindows = [...formData.time_windows];
    newWindows[idx] = { ...newWindows[idx], [field]: val };
    const updated = { ...formData, time_windows: newWindows };
    setFormData(updated);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveInPlace(updated), 500);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      if (onDeleteStop) {
        await onDeleteStop(stop.id);
      } else {
        const res = await fetch(`/api/stops/${stop.id}`, { method: 'DELETE' });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Erreur lors de la suppression de la livraison');
        }
        if (onReloadData) await onReloadData();
      }
      onClose();
    } catch (err) {
      console.error('Error deleting stop:', err);
      alert('Erreur: ' + (err.message || err));
    } finally {
      setIsDeleting(false);
      setIsConfirmingDelete(false);
    }
  };

  const client = clients.find(c => c.id === parseInt(formData.client_id)) || {
    id: stop.client_id || 1,
    name: stop.client_name || 'Client Inconnu',
    address: stop.address || '',
    postal_code: stop.postal_code || '',
    city: stop.city || '',
    roadbook: stop.client_roadbook || ''
  };

  const allStopsList = (() => {
    const combined = [...(stops || []), ...(unassignedStops || [])];
    const map = new Map();
    combined.forEach(s => { if (s && s.id) map.set(s.id, s); });
    return Array.from(map.values());
  })();

  const predecessorEdges = (stopDependencies || []).filter(d => d.successor_stop_id === formData.id);
  const successorEdges = (stopDependencies || []).filter(d => d.predecessor_stop_id === formData.id);

  // Filtered proofs and signatures
  const driverSig = attachments.find(a => a.attachment_type === 'driver_signature');
  const recipientSig = attachments.find(a => a.attachment_type === 'recipient_signature');
  const recipientNameAtt = attachments.find(a => a.attachment_type === 'recipient_signature_name');
  const docAttachments = attachments.filter(a =>
    a.attachment_type !== 'driver_signature' &&
    a.attachment_type !== 'recipient_signature' &&
    a.attachment_type !== 'recipient_signature_name'
  );

  const getStatusColor = () => {
    if (formData.status === 'delivered' || formData.status === 'completed' || formData.status === 'done') return '#10b981';
    if (formData.status === 'failed' || formData.status === 'cancelled') return '#ef4444';
    if (formData.status === 'in_progress') return '#f59e0b';
    return '#38bdf8';
  };

  // Compact input styling
  const inputStyle = {
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '4px',
    color: '#f8fafc',
    fontSize: '0.8rem',
    padding: '4px 8px',
    height: '28px',
    width: '100%',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    fontSize: '0.72rem',
    fontWeight: '700',
    color: '#94a3b8',
    display: 'block',
    marginBottom: '3px',
    whiteSpace: 'nowrap'
  };

  const sectionStyle = {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '6px',
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const sectionHeaderStyle = {
    fontSize: '0.74rem',
    fontWeight: '800',
    color: '#38bdf8',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    borderBottom: '1px solid #334155',
    paddingBottom: '4px',
    marginBottom: '2px'
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div
        className="overlay-card"
        style={{
          width: '920px',
          maxWidth: '96vw',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
          overflow: 'hidden',
          borderRadius: '10px',
          background: '#0f172a',
          border: '1px solid #334155',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.6)'
        }}
      >
        {/* COMPACT HEADER BAR */}
        <div
          style={{
            background: '#1e293b',
            borderBottom: `2px solid ${getStatusColor()}`,
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}
        >
          {/* Left: ID, Stop Number, Type, Status, Date */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.9rem', fontWeight: '800', color: '#94a3b8' }}>#{formData.id}</span>

            <input
              type="text"
              value={formData.stop_number}
              onChange={e => handleFieldChange('stop_number', e.target.value)}
              onBlur={handleFieldBlur}
              style={{
                background: '#0f172a',
                border: '1px solid #334155',
                borderRadius: '4px',
                color: '#fff',
                fontSize: '0.85rem',
                fontWeight: '800',
                padding: '2px 8px',
                height: '26px',
                width: '120px'
              }}
              title="Réf. Stop"
            />

            <select
              value={formData.type || 'delivery'}
              onChange={e => handleImmediateChange('type', e.target.value)}
              style={{
                background: formData.type === 'pickup' ? 'rgba(249, 115, 22, 0.15)' : 'rgba(56, 189, 248, 0.15)',
                color: formData.type === 'pickup' ? '#f97316' : '#38bdf8',
                border: `1px solid ${formData.type === 'pickup' ? '#f97316' : '#38bdf8'}`,
                borderRadius: '4px',
                fontSize: '0.78rem',
                fontWeight: '800',
                padding: '3px 8px',
                height: '26px',
                cursor: 'pointer'
              }}
            >
              <option value="delivery">📦 LIVRAISON</option>
              <option value="pickup">📦↗ RAMASSAGE</option>
            </select>

            <select
              value={formData.status}
              onChange={e => handleImmediateChange('status', e.target.value)}
              style={{
                background: 'rgba(0, 0, 0, 0.3)',
                color: getStatusColor(),
                border: `1px solid ${getStatusColor()}`,
                borderRadius: '4px',
                fontSize: '0.78rem',
                fontWeight: '800',
                padding: '3px 8px',
                height: '26px',
                cursor: 'pointer'
              }}
            >
              <option value="pending">À faire</option>
              <option value="in_progress">En cours</option>
              <option value="delivered">Effectué</option>
              <option value="failed">Échoué</option>
              <option value="cancelled">Annulé</option>
            </select>

            {/* Delivery Date in Header */}
            <input
              type="date"
              value={formData.delivery_date}
              onChange={e => handleFieldChange('delivery_date', e.target.value)}
              onBlur={handleFieldBlur}
              style={{
                background: '#0f172a',
                border: '1px solid #334155',
                borderRadius: '4px',
                color: '#fff',
                fontSize: '0.8rem',
                fontWeight: '700',
                padding: '2px 8px',
                height: '26px',
                cursor: 'pointer'
              }}
              title="Date de livraison / expédition"
            />
          </div>

          {/* Right: Delete button, Save status, Close */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Delete button in header with in-place confirmation */}
            {!isConfirmingDelete ? (
              <button
                type="button"
                onClick={() => setIsConfirmingDelete(true)}
                style={{
                  background: 'rgba(239, 68, 68, 0.12)',
                  color: '#ef4444',
                  border: '1px solid rgba(239, 68, 68, 0.35)',
                  borderRadius: '4px',
                  padding: '2px 8px',
                  fontSize: '0.74rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  height: '26px'
                }}
                title="Supprimer ce stop"
              >
                <Trash2 size={13} />
                <span>Supprimer</span>
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid #ef4444', padding: '2px 6px', borderRadius: '4px', height: '26px' }}>
                <span style={{ fontSize: '0.72rem', color: '#fca5a5', fontWeight: '700' }}>Supprimer ?</span>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  style={{
                    background: '#dc2626',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '3px',
                    padding: '2px 6px',
                    fontSize: '0.7rem',
                    fontWeight: '800',
                    cursor: isDeleting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isDeleting ? '...' : 'Oui'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsConfirmingDelete(false)}
                  disabled={isDeleting}
                  style={{
                    background: 'transparent',
                    color: '#94a3b8',
                    border: '1px solid #475569',
                    borderRadius: '3px',
                    padding: '2px 5px',
                    fontSize: '0.7rem',
                    cursor: 'pointer'
                  }}
                >
                  Non
                </button>
              </div>
            )}

            {saveStatus === 'saving' && (
              <span style={{ fontSize: '0.75rem', color: '#38bdf8', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <RefreshCw size={12} className="spin" /> Sauvegarde...
              </span>
            )}
            {saveStatus === 'saved' && (
              <span style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Check size={13} /> Enregistré
              </span>
            )}
            {saveStatus === 'error' && (
              <span style={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <AlertCircle size={12} /> Erreur
              </span>
            )}

            <button
              type="button"
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px', display: 'flex' }}
              title="Fermer"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* UNIFIED SCROLLABLE SINGLE VIEW CONTENT */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          
          {/* ROW 1: 2-COLUMN GRID (CLIENT & MISSION vs HORAIRES & TEMPS DE SERVICE) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            
            {/* COLUMN LEFT: CLIENT & GENERAL */}
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <MapPin size={13} />
                <span>Client & Affectation</span>
              </div>

              {/* Client Selection */}
              <div>
                <label style={labelStyle}>Client</label>
                <select
                  value={formData.client_id}
                  onChange={e => handleImmediateChange('client_id', parseInt(e.target.value))}
                  style={{ ...inputStyle, fontWeight: '700' }}
                >
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name} {c.city ? `(${c.city})` : ''} [#{c.id}]
                    </option>
                  ))}
                </select>
              </div>

              {/* Client Info Banner */}
              <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '4px', padding: '6px 10px', fontSize: '0.75rem', color: '#94a3b8' }}>
                <div style={{ color: '#fff', fontWeight: '700' }}>{client.name}</div>
                <div>{client.address ? `${client.address}, ` : ''}{client.postal_code} {client.city}</div>
                {client.roadbook && (
                  <div style={{ color: '#38bdf8', marginTop: '2px', fontStyle: 'italic' }}>
                    Roadbook: {client.roadbook}
                  </div>
                )}
              </div>

              {/* Mission & Tournée Modèle */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={labelStyle}>Mission</label>
                  <select
                    value={formData.job_id || ''}
                    onChange={e => handleImmediateChange('job_id', e.target.value ? parseInt(e.target.value) : null)}
                    style={inputStyle}
                  >
                    <option value="">Non assigné (Bucket)</option>
                    {jobs.map(j => (
                      <option key={j.id} value={j.id}>{j.display_name || `Mission #${j.id}`}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>Tournée modèle</label>
                  <select
                    value={formData.default_round_id || ''}
                    onChange={e => handleImmediateChange('default_round_id', e.target.value ? parseInt(e.target.value) : null)}
                    style={inputStyle}
                  >
                    <option value="">Aucune</option>
                    {rounds.map(r => (
                      <option key={r.id} value={r.id}>{r.name} ({r.code || `#${r.id}`})</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* COLUMN RIGHT: HORAIRES & TEMPS DE SERVICE */}
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <Clock size={13} />
                <span>Horaires, Service & Départs</span>
              </div>

              {/* Estimated vs Real Service & Times */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={labelStyle}>Arrivée est.</label>
                  <input
                    type="time"
                    value={formData.estimated_arrival}
                    onChange={e => handleFieldChange('estimated_arrival', e.target.value)}
                    onBlur={handleFieldBlur}
                    style={{ ...inputStyle, color: '#38bdf8', fontWeight: '700' }}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Durée est. (min)</label>
                  <input
                    type="number"
                    step="1"
                    value={formData.service_time_mins}
                    onChange={e => handleFieldChange('service_time_mins', e.target.value)}
                    onBlur={handleFieldBlur}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Départ est.</label>
                  <input
                    type="time"
                    value={addMinutesToTime(formData.estimated_arrival, formData.service_time_mins)}
                    onChange={e => handleEstimatedDepartureChange(e.target.value)}
                    onBlur={handleFieldBlur}
                    style={{ ...inputStyle, color: '#38bdf8', fontWeight: '700' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={labelStyle}>Arrivée réelle</label>
                  <input
                    type="time"
                    value={formData.actual_arrival_time}
                    onChange={e => handleActualArrivalChange(e.target.value)}
                    onBlur={handleFieldBlur}
                    style={{ ...inputStyle, color: '#10b981', fontWeight: '700' }}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Départ réel</label>
                  <input
                    type="time"
                    value={formData.real_departure_time}
                    onChange={e => handleRealDepartureChange(e.target.value)}
                    onBlur={handleFieldBlur}
                    style={{ ...inputStyle, color: '#10b981', fontWeight: '700' }}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Durée réelle (min)</label>
                  <input
                    type="number"
                    step="1"
                    placeholder="-"
                    value={formData.real_duration_mins}
                    onChange={e => handleRealDurationChange(e.target.value)}
                    onBlur={handleFieldBlur}
                    style={{ ...inputStyle, color: '#10b981', fontWeight: '700' }}
                  />
                </div>
              </div>

              {/* Time Windows / Créneaux Horaires */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>Créneaux autorisés</label>
                  <button
                    type="button"
                    onClick={handleAddTimeWindow}
                    style={{
                      background: 'rgba(56, 189, 248, 0.15)',
                      color: '#38bdf8',
                      border: '1px solid #38bdf8',
                      borderRadius: '4px',
                      padding: '1px 6px',
                      fontSize: '0.68rem',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    + Créneau
                  </button>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {formData.time_windows.map((tw, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: '#0f172a',
                        border: '1px solid #334155',
                        borderRadius: '4px',
                        padding: '2px 8px',
                        fontSize: '0.78rem'
                      }}
                    >
                      <Clock size={11} style={{ color: '#38bdf8', opacity: 0.8 }} />
                      <input
                        type="time"
                        className="clean-time-input"
                        value={tw.start || '08:00'}
                        onChange={e => handleTimeWindowFieldChange(idx, 'start', e.target.value)}
                        onBlur={handleFieldBlur}
                        style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '0.78rem', width: '48px', outline: 'none', textAlign: 'center' }}
                      />
                      <span style={{ color: '#64748b', fontWeight: '700' }}>—</span>
                      <input
                        type="time"
                        className="clean-time-input"
                        value={tw.end || '18:00'}
                        onChange={e => handleTimeWindowFieldChange(idx, 'end', e.target.value)}
                        onBlur={handleFieldBlur}
                        style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '0.78rem', width: '48px', outline: 'none', textAlign: 'center' }}
                      />
                      {formData.time_windows.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveTimeWindow(idx)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' }}
                          title="Supprimer ce créneau"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>

          {/* ROW 2: 3-COLUMN COMPACT ROW (CHARGE/VOLUME, INDEXES, DÉPENDANCES) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1.3fr', gap: '12px' }}>
            
            {/* CHARGE & ENCAISSEMENT */}
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <Package size={13} />
                <span>Charge & Encaissement</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
                <div>
                  <label style={labelStyle}>Poids (kg)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.weight_kg}
                    onChange={e => handleFieldChange('weight_kg', e.target.value)}
                    onBlur={handleFieldBlur}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Volume (m³)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.volume_m3}
                    onChange={e => handleFieldChange('volume_m3', e.target.value)}
                    onBlur={handleFieldBlur}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>CA (CHF)</label>
                  <input
                    type="number"
                    step="0.05"
                    value={formData.revenue}
                    onChange={e => handleFieldChange('revenue', e.target.value)}
                    onBlur={handleFieldBlur}
                    style={{ ...inputStyle, color: '#10b981', fontWeight: '700' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '6px' }}>
                <div>
                  <label style={labelStyle}>Règlement</label>
                  <select
                    value={formData.payment_type || 'none'}
                    onChange={e => handleImmediateChange('payment_type', e.target.value)}
                    style={inputStyle}
                  >
                    <option value="none">Aucun (Compte)</option>
                    <option value="cash">Espèces</option>
                    <option value="card">Carte bancaire</option>
                    <option value="invoice">Sur Facture</option>
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>Montant (CHF)</label>
                  <input
                    type="number"
                    step="0.05"
                    value={formData.amount_paid}
                    onChange={e => handleFieldChange('amount_paid', e.target.value)}
                    onBlur={handleFieldBlur}
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>

            {/* SÉQUENCE & INDEX */}
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <span>Ordre & Index</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
                <div>
                  <label style={labelStyle}>Seq</label>
                  <input
                    type="number"
                    step="1"
                    value={formData.sequence_order}
                    onChange={e => handleFieldChange('sequence_order', e.target.value)}
                    onBlur={handleFieldBlur}
                    style={{ ...inputStyle, fontWeight: '700' }}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Index est.</label>
                  <input
                    type="number"
                    step="1"
                    value={formData.estimated_index}
                    onChange={e => handleFieldChange('estimated_index', e.target.value)}
                    onBlur={handleFieldBlur}
                    style={{ ...inputStyle, color: '#38bdf8', fontWeight: '700' }}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Index réel</label>
                  <input
                    type="number"
                    step="1"
                    placeholder="-"
                    value={formData.real_index}
                    onChange={e => handleFieldChange('real_index', e.target.value)}
                    onBlur={handleFieldBlur}
                    style={{ ...inputStyle, color: '#10b981', fontWeight: '700' }}
                  />
                </div>
              </div>
            </div>

            {/* DÉPENDANCES RELATIVES */}
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <Link size={13} />
                <span>Dépendances</span>
              </div>

              {/* Avant ce stop */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label style={labelStyle}>Avant ce stop</label>
                  <select
                    value=""
                    onChange={async (e) => {
                      const val = e.target.value;
                      if (!val) return;
                      await fetch('/api/stop-dependencies', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ predecessor_stop_id: parseInt(val), successor_stop_id: formData.id, dependency_type: 'precedence' })
                      });
                      if (onReloadData) await onReloadData();
                    }}
                    style={{ background: '#0f172a', border: '1px solid #334155', color: '#38bdf8', fontSize: '0.68rem', borderRadius: '3px', padding: '1px 4px' }}
                  >
                    <option value="">+ Ajouter...</option>
                    {allStopsList.filter(s => s.id !== formData.id && !predecessorEdges.some(d => d.predecessor_stop_id === s.id)).map(s => (
                      <option key={s.id} value={s.id}>{s.stop_number || `STOP-${s.id}`}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', minHeight: '22px' }}>
                  {predecessorEdges.map(dep => {
                    const pStop = allStopsList.find(s => s.id === dep.predecessor_stop_id);
                    return (
                      <span key={dep.id || dep.predecessor_stop_id} style={{ display: 'flex', alignItems: 'center', gap: '3px', background: '#0f172a', border: '1px solid #334155', borderRadius: '3px', padding: '1px 5px', fontSize: '0.72rem', color: '#38bdf8' }}>
                        {pStop?.stop_number || `STOP-${dep.predecessor_stop_id}`}
                        <button
                          type="button"
                          onClick={async () => {
                            await fetch('/api/stop-dependencies', {
                              method: 'DELETE',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ predecessor_stop_id: dep.predecessor_stop_id, successor_stop_id: formData.id })
                            });
                            if (onReloadData) await onReloadData();
                          }}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0 }}
                        >
                          <X size={11} />
                        </button>
                      </span>
                    );
                  })}
                  {predecessorEdges.length === 0 && (
                    <span style={{ fontSize: '0.7rem', color: '#64748b', fontStyle: 'italic' }}>Aucun</span>
                  )}
                </div>
              </div>

              {/* Après ce stop */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label style={labelStyle}>Après ce stop</label>
                  <select
                    value=""
                    onChange={async (e) => {
                      const val = e.target.value;
                      if (!val) return;
                      await fetch('/api/stop-dependencies', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ predecessor_stop_id: formData.id, successor_stop_id: parseInt(val), dependency_type: 'precedence' })
                      });
                      if (onReloadData) await onReloadData();
                    }}
                    style={{ background: '#0f172a', border: '1px solid #334155', color: '#10b981', fontSize: '0.68rem', borderRadius: '3px', padding: '1px 4px' }}
                  >
                    <option value="">+ Ajouter...</option>
                    {allStopsList.filter(s => s.id !== formData.id && !successorEdges.some(d => d.successor_stop_id === s.id)).map(s => (
                      <option key={s.id} value={s.id}>{s.stop_number || `STOP-${s.id}`}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', minHeight: '22px' }}>
                  {successorEdges.map(dep => {
                    const sStop = allStopsList.find(s => s.id === dep.successor_stop_id);
                    return (
                      <span key={dep.id || dep.successor_stop_id} style={{ display: 'flex', alignItems: 'center', gap: '3px', background: '#0f172a', border: '1px solid #334155', borderRadius: '3px', padding: '1px 5px', fontSize: '0.72rem', color: '#10b981' }}>
                        {sStop?.stop_number || `STOP-${dep.successor_stop_id}`}
                        <button
                          type="button"
                          onClick={async () => {
                            await fetch('/api/stop-dependencies', {
                              method: 'DELETE',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ predecessor_stop_id: formData.id, successor_stop_id: dep.successor_stop_id })
                            });
                            if (onReloadData) await onReloadData();
                          }}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0 }}
                        >
                          <X size={11} />
                        </button>
                      </span>
                    );
                  })}
                  {successorEdges.length === 0 && (
                    <span style={{ fontSize: '0.7rem', color: '#64748b', fontStyle: 'italic' }}>Aucun</span>
                  )}
                </div>
              </div>
            </div>

          </div>

          {/* ROW 3: PREUVES DE LIVRAISON, SIGNATURES & BONS DE RETOUR */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '12px' }}>
            
            {/* SECTION GAUCHE: SIGNATURES & PREUVES PHOTOS */}
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <ShieldCheck size={14} color="#22c55e" />
                <span>Signatures & Preuves de Livraison (POD)</span>
                <span style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 'normal', marginLeft: 'auto' }}>
                  {attachments.length} élément{attachments.length > 1 ? 's' : ''}
                </span>
              </div>

              {/* Signatures: Livreur & Client */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                
                {/* Livreur */}
                <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <PenTool size={11} color="#38bdf8" /> Signature Livreur
                    </span>
                    {driverSig && (
                      <span style={{ fontSize: '0.65rem', color: '#22c55e', background: 'rgba(34, 197, 94, 0.15)', padding: '1px 4px', borderRadius: '3px', fontWeight: '700' }}>
                        Signé
                      </span>
                    )}
                  </div>
                  {driverSig ? (
                    <div
                      style={{ background: '#ffffff', borderRadius: '4px', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '52px', cursor: 'pointer' }}
                      onClick={() => setSelectedProofImage(resolveAssetUrl(driverSig.value))}
                      title="Cliquer pour agrandir"
                    >
                      <img src={resolveAssetUrl(driverSig.value)} alt="Signature Livreur" style={{ maxHeight: '48px', maxWidth: '100%', objectFit: 'contain' }} />
                    </div>
                  ) : (
                    <div style={{ background: '#1e293b', border: '1px dashed #334155', borderRadius: '4px', minHeight: '52px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: '#64748b' }}>
                      Non signée
                    </div>
                  )}
                </div>

                {/* Client Destinataire */}
                <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <PenTool size={11} color="#22c55e" /> Signature Client
                    </span>
                    {recipientSig && (
                      <span style={{ fontSize: '0.65rem', color: '#22c55e', background: 'rgba(34, 197, 94, 0.15)', padding: '1px 4px', borderRadius: '3px', fontWeight: '700' }}>
                        Signé
                      </span>
                    )}
                  </div>
                  {recipientSig ? (
                    <div>
                      <div
                        style={{ background: '#ffffff', borderRadius: '4px', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '52px', cursor: 'pointer' }}
                        onClick={() => setSelectedProofImage(resolveAssetUrl(recipientSig.value))}
                        title="Cliquer pour agrandir"
                      >
                        <img src={resolveAssetUrl(recipientSig.value)} alt="Signature Client" style={{ maxHeight: '48px', maxWidth: '100%', objectFit: 'contain' }} />
                      </div>
                      {recipientNameAtt?.value && (
                        <div style={{ fontSize: '0.68rem', color: '#cbd5e1', marginTop: '2px', textAlign: 'center' }}>
                          Signataire : <b>{recipientNameAtt.value}</b>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ background: '#1e293b', border: '1px dashed #334155', borderRadius: '4px', minHeight: '52px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: '#64748b' }}>
                      Non signée
                    </div>
                  )}
                </div>

              </div>

              {/* Photos POD & Documents attachés */}
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#94a3b8', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <ImageIcon size={11} /> Photos sur place & Pièces jointes ({docAttachments.length})
                </div>
                {docAttachments.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {docAttachments.map(att => {
                      const isImg = att.value && (att.value.endsWith('.png') || att.value.endsWith('.jpg') || att.value.endsWith('.jpeg') || att.value.endsWith('.webp') || att.value.startsWith('data:image'));
                      let badgeLabel = 'Photo';
                      if (att.attachment_type === 'proof_of_payment_photo') badgeLabel = '💳 Paiement';
                      else if (att.attachment_type === 'order_pdf') badgeLabel = '📄 Bon PDF';
                      else if (att.attachment_type === 'other_document') badgeLabel = '📎 Document';

                      return (
                        <div
                          key={att.id}
                          style={{
                            background: '#0f172a',
                            border: '1px solid #334155',
                            borderRadius: '6px',
                            padding: '4px 6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '0.72rem'
                          }}
                        >
                          {isImg ? (
                            <div
                              onClick={() => setSelectedProofImage(resolveAssetUrl(att.value))}
                              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                              title="Cliquer pour agrandir"
                            >
                              <img src={resolveAssetUrl(att.value)} alt={badgeLabel} style={{ width: '28px', height: '28px', borderRadius: '3px', objectFit: 'cover', border: '1px solid #475569' }} />
                            </div>
                          ) : (
                            <FileText size={18} color="#38bdf8" />
                          )}
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <a
                              href={resolveAssetUrl(att.value)}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: '#38bdf8', textDecoration: 'none', fontWeight: '600', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '2px' }}
                            >
                              <span>{badgeLabel}</span>
                              <ExternalLink size={9} />
                            </a>
                            {att.created_at && (
                              <span style={{ fontSize: '0.62rem', color: '#64748b' }}>
                                {new Date(att.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.7rem', color: '#64748b', fontStyle: 'italic', background: '#0f172a', padding: '6px 10px', borderRadius: '4px', border: '1px dashed #334155' }}>
                    Aucune photo ou document POD enregistré pour cette livraison.
                  </div>
                )}
              </div>

            </div>

            {/* SECTION DROITE: BONS DE RETOUR & OBJETS CONSIGNÉS */}
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <Package size={14} color="#f59e0b" />
                <span>Bons de Retour & Objets Consignés</span>
                <span style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 'normal', marginLeft: 'auto' }}>
                  {returnTickets.length} bon{returnTickets.length > 1 ? 's' : ''}
                </span>
              </div>

              {returnTickets.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '180px', overflowY: 'auto' }}>
                  {returnTickets.map(ticket => (
                    <div key={ticket.id} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #1e293b', paddingBottom: '3px' }}>
                        <span style={{ fontWeight: '700', fontSize: '0.74rem', color: '#f8fafc' }}>
                          Bon #{ticket.id} {ticket.order_id ? `(${ticket.order_id})` : ''}
                        </span>
                        <span style={{ fontSize: '0.65rem', background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', padding: '1px 5px', borderRadius: '3px', fontWeight: '700' }}>
                          {ticket.status || 'Validé'}
                        </span>
                      </div>

                      {ticket.lines && ticket.lines.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {ticket.lines.map((l, lIdx) => (
                            <div key={lIdx} style={{ fontSize: '0.7rem', display: 'flex', justifyContent: 'space-between', color: '#cbd5e1' }}>
                              <span>• {l.object_name || 'Objet'}</span>
                              <span style={{ fontWeight: '700', color: l.amount > 0 ? '#22c55e' : '#cbd5e1' }}>
                                {l.amount > 0 ? `+${l.amount}` : l.amount} × {parseFloat(l.unit_price || 0).toFixed(2)} = {((l.amount || 0) * (l.unit_price || 0)).toFixed(2)} CHF
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.68rem', color: '#64748b', fontStyle: 'italic' }}>Aucune ligne d'objet</div>
                      )}

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px dashed #334155', paddingTop: '3px', marginTop: '2px', fontSize: '0.72rem' }}>
                        <span style={{ color: '#94a3b8' }}>Total validé :</span>
                        <span style={{ fontWeight: '800', color: '#22c55e' }}>
                          {parseFloat(ticket.validated_total_value || 0).toFixed(2)} CHF
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontStyle: 'italic', background: '#0f172a', padding: '8px 10px', borderRadius: '4px', border: '1px dashed #334155', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80px' }}>
                  Aucun bon de retour ou objet consigné enregistré pour ce stop.
                </div>
              )}

            </div>

          </div>

          {/* ROW 4: INSTRUCTIONS & NOTES CHAUFFEUR (2-COLUMN TEXTAREAS) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={sectionStyle}>
              <label style={labelStyle}>Instructions</label>
              <textarea
                rows={2}
                value={formData.instructions}
                onChange={e => handleFieldChange('instructions', e.target.value)}
                onBlur={handleFieldBlur}
                placeholder="Instructions livraison..."
                style={{
                  ...inputStyle,
                  height: 'auto',
                  minHeight: '44px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={sectionStyle}>
              <label style={labelStyle}>Notes chauffeur</label>
              <textarea
                rows={2}
                value={formData.driver_notes}
                onChange={e => handleFieldChange('driver_notes', e.target.value)}
                onBlur={handleFieldBlur}
                placeholder="Notes chauffeur, code porte, accès..."
                style={{
                  ...inputStyle,
                  height: 'auto',
                  minHeight: '44px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>
          </div>

          {/* ROW 5: METADATA (JSON) */}
          <div style={sectionStyle}>
            <label style={labelStyle}>Métadonnées (JSON)</label>
            <textarea
              rows={2}
              value={formData.metadata || ''}
              onChange={e => handleFieldChange('metadata', e.target.value)}
              onBlur={handleFieldBlur}
              placeholder='{"cle": "valeur", ...}'
              style={{
                ...inputStyle,
                height: 'auto',
                minHeight: '44px',
                resize: 'vertical',
                fontFamily: 'monospace',
                fontSize: '0.8rem'
              }}
            />
          </div>

        </div>

        {/* LIGHTBOX MODAL FOR PREVIEWING PROOFS & SIGNATURES FULL SIZE */}
        {selectedProofImage && (
          <div
            className="modal-overlay"
            style={{ zIndex: 1200, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setSelectedProofImage(null)}
          >
            <div
              style={{ position: 'relative', maxWidth: '85vw', maxHeight: '85vh', background: '#1e293b', padding: '10px', borderRadius: '8px', border: '1px solid #334155', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.7)' }}
              onClick={e => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setSelectedProofImage(null)}
                style={{ position: 'absolute', top: '-10px', right: '-10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }}
                title="Fermer"
              >
                <X size={15} />
              </button>
              <img src={selectedProofImage} alt="Preuve de livraison" style={{ maxWidth: '80vw', maxHeight: '80vh', objectFit: 'contain', display: 'block', borderRadius: '4px' }} />
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
